/**
 * Keyframed curves and color gradients: evaluation and editing helpers for
 * the reusable CurveEditor / GradientEditor controls (components/ui).
 *
 * The JSON shapes and the evaluation match the Prism engine exactly
 * (engine/crates/prism_core/src/particles.rs `Curve`, `Gradient`), so any
 * component that stores these values previews what the engine will do:
 *
 * - curve keys `[[time, value], ...]`, time ascending in 0..1, clamped to the
 *   first / last key outside them; linear, or `smooth` (a cubic Hermite spline
 *   with flat end tangents and monotone, Fritsch-Carlson limited, inner
 *   tangents: it eases in and out and never overshoots between two keys);
 * - gradients `{ colorKeys: [[t, r, g, b]], alphaKeys: [[t, a]], mode }`
 *   (sRGB 0..1, `blend` or `fixed` steps; no keys = white / opaque).
 *
 * Pure module (no DOM, no React).
 */

export type CurveKey = [number, number];
export type CurveKeys = CurveKey[];

export interface Gradient {
  colorKeys?: [number, number, number, number][];
  alphaKeys?: [number, number][];
  mode?: 'blend' | 'fixed';
}

export type Rgba = [number, number, number, number];

/** Round to 3 decimals (keeps the JSON readable). */
export const r3 = (v: number) => Math.round(v * 1000) / 1000;
export const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function isCurveKeys(v: unknown): v is CurveKeys {
  return Array.isArray(v) && v.every((k) => Array.isArray(k) && k.length === 2 && isNum(k[0]) && isNum(k[1]));
}

// ---------------------------------------------------------------------------
// Curve evaluation

/** Linear evaluation (the engine's `Curve::eval`): clamped outside the keys, empty = 0. */
export function evalLinear(keys: readonly CurveKey[], t: number): number {
  const first = keys[0];
  const last = keys[keys.length - 1];
  if (!first || !last) return 0;
  if (keys.length === 1 || t <= first[0]) return first[1];
  for (let i = 1; i < keys.length; i++) {
    const a = keys[i - 1]!;
    const b = keys[i]!;
    if (t <= b[0]) {
      const span = b[0] - a[0];
      const f = span > 1e-6 ? (t - a[0]) / span : 1;
      return a[1] + (b[1] - a[1]) * f;
    }
  }
  return last[1];
}

function slope(keys: readonly CurveKey[], i: number): number {
  const h = keys[i + 1]![0] - keys[i]![0];
  return h > 1e-6 ? (keys[i + 1]![1] - keys[i]![1]) / h : 0;
}

/** Tangent at key `i` of a smooth curve (flat at the ends and at extrema). */
export function smoothTangent(keys: readonly CurveKey[], i: number): number {
  if (i <= 0 || i >= keys.length - 1) return 0;
  const d0 = slope(keys, i - 1);
  const d1 = slope(keys, i);
  if (d0 * d1 <= 0) return 0;
  const m = (d0 + d1) * 0.5;
  const limit = 3 * Math.min(Math.abs(d0), Math.abs(d1));
  return Math.sign(m) * Math.min(Math.abs(m), limit);
}

/** Smooth evaluation (the engine's `Curve::eval_smooth`). */
export function evalSmooth(keys: readonly CurveKey[], t: number): number {
  const n = keys.length;
  if (n < 2) return evalLinear(keys, t);
  if (t <= keys[0]![0]) return keys[0]![1];
  if (t >= keys[n - 1]![0]) return keys[n - 1]![1];
  let i = 0;
  while (i < n - 2 && t > keys[i + 1]![0]) i++;
  const a = keys[i]!;
  const b = keys[i + 1]!;
  const h = b[0] - a[0];
  if (h <= 1e-6) return b[1];
  const m0 = smoothTangent(keys, i);
  const m1 = smoothTangent(keys, i + 1);
  const s = (t - a[0]) / h;
  const s2 = s * s;
  const s3 = s2 * s;
  return (2 * s3 - 3 * s2 + 1) * a[1] + (s3 - 2 * s2 + s) * h * m0 + (-2 * s3 + 3 * s2) * b[1] + (s3 - s2) * h * m1;
}

export function evalCurve(keys: readonly CurveKey[], t: number, smooth = false): number {
  return smooth ? evalSmooth(keys, t) : evalLinear(keys, t);
}

/** `samples + 1` points `[t, value]` along the curve (for drawing). */
export function sampleCurve(keys: readonly CurveKey[], smooth: boolean, samples = 64): CurveKey[] {
  if (!smooth) {
    const inner = keys.filter((k) => k[0] > 0 && k[0] < 1);
    return [[0, evalLinear(keys, 0)], ...inner.map((k) => [k[0], k[1]] as CurveKey), [1, evalLinear(keys, 1)]];
  }
  const out: CurveKey[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    out.push([t, evalSmooth(keys, t)]);
  }
  return out;
}

/** [lowest, highest] value of the curves (keys bound smooth curves too), at least `base`. */
export function curvesRange(curves: readonly (readonly CurveKey[])[], base: [number, number] = [0, 1]): [number, number] {
  let lo = base[0];
  let hi = base[1];
  for (const c of curves)
    for (const k of c) {
      lo = Math.min(lo, k[1]);
      hi = Math.max(hi, k[1]);
    }
  return [lo, hi];
}

// ---------------------------------------------------------------------------
// Curve editing (each returns new keys and the edited key's new index)

export interface KeyEdit {
  keys: CurveKeys;
  index: number;
}

const sorted = (keys: CurveKeys, tracked: CurveKey): KeyEdit => {
  const out = [...keys].sort((a, b) => a[0] - b[0]);
  return { keys: out, index: out.indexOf(tracked) };
};

/** Adds a key at time `t` (value: on the curve unless given). */
export function addCurveKey(keys: readonly CurveKey[], t: number, value?: number, smooth = false): KeyEdit {
  const key: CurveKey = [r3(clamp01(t)), r3(value ?? evalCurve(keys, t, smooth))];
  const same = keys.findIndex((k) => Math.abs(k[0] - key[0]) < 1e-4);
  if (same >= 0) {
    const out = keys.map((k) => [k[0], k[1]] as CurveKey);
    out[same] = key;
    return { keys: out, index: same };
  }
  return sorted([...keys.map((k) => [k[0], k[1]] as CurveKey), key], key);
}

/** Moves key `index` to (`t`, `value`); time is clamped to 0..1 and keys stay sorted. */
export function moveCurveKey(keys: readonly CurveKey[], index: number, t: number, value: number): KeyEdit {
  if (!keys[index]) return { keys: keys.map((k) => [k[0], k[1]] as CurveKey), index: -1 };
  const key: CurveKey = [r3(clamp01(t)), r3(value)];
  const out = keys.map((k, i) => (i === index ? key : ([k[0], k[1]] as CurveKey)));
  return sorted(out, key);
}

/** Removes key `index` (the last key is kept: a curve needs one). */
export function removeCurveKey(keys: readonly CurveKey[], index: number): CurveKeys {
  if (keys.length <= 1 || !keys[index]) return keys.map((k) => [k[0], k[1]] as CurveKey);
  return keys.filter((_, i) => i !== index).map((k) => [k[0], k[1]] as CurveKey);
}

/** Every key's value times `factor` (e.g. to fold a multiplier into the keys). */
export function scaleCurve(keys: readonly CurveKey[], factor: number): CurveKeys {
  return keys.map(([t, v]) => [t, r3(v * factor)] as CurveKey);
}

/** Mirror in time (`t -> 1 - t`). */
export function reverseCurve(keys: readonly CurveKey[]): CurveKeys {
  return keys.map(([t, v]) => [r3(1 - t), v] as CurveKey).sort((a, b) => a[0] - b[0]);
}

export interface CurvePreset {
  name: string;
  keys: CurveKeys;
  smooth: boolean;
}

/** Shapes offered by the curve editor's preset menu (0..1, scaled by the curve's range). */
export const CURVE_PRESETS: readonly CurvePreset[] = [
  { name: 'Constant', keys: [[0, 1], [1, 1]], smooth: false },
  { name: 'Linear up', keys: [[0, 0], [1, 1]], smooth: false },
  { name: 'Linear down', keys: [[0, 1], [1, 0]], smooth: false },
  { name: 'Ease in-out', keys: [[0, 0], [1, 1]], smooth: true },
  { name: 'Ease out (fade)', keys: [[0, 1], [1, 0]], smooth: true },
  { name: 'Ease in', keys: [[0, 0], [0.6, 0.25], [1, 1]], smooth: true },
  { name: 'Ease out', keys: [[0, 0], [0.4, 0.75], [1, 1]], smooth: true },
  { name: 'Bell', keys: [[0, 0], [0.5, 1], [1, 0]], smooth: true },
  { name: 'Pop', keys: [[0, 0], [0.1, 1], [1, 0.6]], smooth: true },
  { name: 'Fade in / out', keys: [[0, 0], [0.15, 1], [0.8, 1], [1, 0]], smooth: false },
];

/** A preset's keys scaled into [lo, hi]. */
export function presetKeys(preset: CurvePreset, lo = 0, hi = 1): CurveKeys {
  return preset.keys.map(([t, v]) => [t, r3(lo + (hi - lo) * v)] as CurveKey);
}

// ---------------------------------------------------------------------------
// Gradients

export const WHITE: Rgba = [1, 1, 1, 1];
/** Unity's limit: at most eight color and eight alpha keys. */
export const MAX_GRADIENT_KEYS = 8;

export function rgba(c: unknown): Rgba {
  const a = Array.isArray(c) ? c : [];
  const g = (i: number) => (isNum(a[i]) ? (a[i] as number) : 1);
  return [g(0), g(1), g(2), g(3)];
}

export function isGradient(v: unknown): v is Gradient {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const ok = (keys: unknown, n: number) =>
    keys === undefined || (Array.isArray(keys) && keys.every((k) => Array.isArray(k) && k.length === n && k.every(isNum)));
  return ok(o.colorKeys, 4) && ok(o.alphaKeys, 2) && (o.mode === undefined || o.mode === 'blend' || o.mode === 'fixed');
}

/** Same as the engine's `Gradient::eval`. */
export function evalGradient(g: Gradient | undefined, t: number): Rgba {
  const ck = g?.colorKeys ?? [];
  const ak = g?.alphaKeys ?? [];
  if (g?.mode === 'fixed') {
    const c = ck.find((k) => t <= k[0]) ?? ck[ck.length - 1];
    const a = ak.find((k) => t <= k[0]) ?? ak[ak.length - 1];
    return [c ? c[1] : 1, c ? c[2] : 1, c ? c[3] : 1, a ? a[1] : 1];
  }
  const ch = (i: number) => (ck.length === 0 ? 1 : evalLinear(ck.map((k) => [k[0], k[i]] as CurveKey), t));
  const alpha = ak.length === 0 ? 1 : evalLinear(ak, t);
  return [ch(1), ch(2), ch(3), alpha];
}

/** Gradient without empty key lists or the default mode. */
export function cleanGradient(g: Gradient): Gradient {
  const out: Gradient = {};
  if (g.colorKeys && g.colorKeys.length > 0) out.colorKeys = g.colorKeys;
  if (g.alphaKeys && g.alphaKeys.length > 0) out.alphaKeys = g.alphaKeys;
  if (g.mode === 'fixed') out.mode = 'fixed';
  return out;
}

export function gradientBetween(a: Rgba, b: Rgba): Gradient {
  const same = a.every((x, i) => x === b[i]);
  return cleanGradient({
    colorKeys: same ? [[0, a[0], a[1], a[2]]] : [[0, a[0], a[1], a[2]], [1, b[0], b[1], b[2]]],
    alphaKeys: a[3] === 1 && b[3] === 1 ? [] : [[0, a[3]], [1, b[3]]],
  });
}

export type GradientKeyKind = 'color' | 'alpha';

export interface GradientKeyEdit {
  gradient: Gradient;
  index: number;
}

function withKeys(g: Gradient, kind: GradientKeyKind, keys: number[][]): Gradient {
  return cleanGradient(
    kind === 'color'
      ? { ...g, colorKeys: keys as Gradient['colorKeys'] }
      : { ...g, alphaKeys: keys as Gradient['alphaKeys'] },
  );
}

const keysOf = (g: Gradient, kind: GradientKeyKind): number[][] =>
  ((kind === 'color' ? g.colorKeys : g.alphaKeys) ?? []).map((k) => [...k]);

/**
 * Adds a key of `kind` at time `t` holding the gradient's current color /
 * alpha there. Returns the gradient unchanged (index -1) at the key limit.
 */
export function addGradientKey(g: Gradient, kind: GradientKeyKind, t: number): GradientKeyEdit {
  const keys = keysOf(g, kind);
  if (keys.length >= MAX_GRADIENT_KEYS) return { gradient: g, index: -1 };
  const time = r3(clamp01(t));
  const [r, gr, b, a] = evalGradient(g, time).map(r3) as Rgba;
  const key = kind === 'color' ? [time, r, gr, b] : [time, a];
  const next = [...keys, key].sort((x, y) => x[0]! - y[0]!);
  return { gradient: withKeys(g, kind, next), index: next.indexOf(key) };
}

/** Replaces key `index` of `kind` (time clamped, keys kept sorted). */
export function setGradientKey(g: Gradient, kind: GradientKeyKind, index: number, key: number[]): GradientKeyEdit {
  const keys = keysOf(g, kind);
  if (!keys[index]) return { gradient: g, index: -1 };
  const k = [r3(clamp01(key[0] ?? 0)), ...key.slice(1).map((x) => r3(clamp01(x)))];
  keys[index] = k;
  const next = keys.sort((x, y) => x[0]! - y[0]!);
  return { gradient: withKeys(g, kind, next), index: next.indexOf(k) };
}

/** Removes key `index` of `kind` (a gradient keeps at least one color key). */
export function removeGradientKey(g: Gradient, kind: GradientKeyKind, index: number): Gradient {
  const keys = keysOf(g, kind);
  if (!keys[index] || (kind === 'color' && keys.length <= 1)) return g;
  return withKeys(
    g,
    kind,
    keys.filter((_, i) => i !== index),
  );
}

/** Mirror in time. */
export function reverseGradient(g: Gradient): Gradient {
  const flip = (keys: number[][] | undefined) => (keys ?? []).map((k) => [r3(1 - k[0]!), ...k.slice(1)]).sort((a, b) => a[0]! - b[0]!);
  return cleanGradient({ ...g, colorKeys: flip(g.colorKeys) as Gradient['colorKeys'], alphaKeys: flip(g.alphaKeys) as Gradient['alphaKeys'] });
}

export const GRADIENT_PRESETS: readonly { name: string; gradient: Gradient }[] = [
  { name: 'White', gradient: { colorKeys: [[0, 1, 1, 1]] } },
  { name: 'Fade out', gradient: { alphaKeys: [[0, 1], [1, 0]] } },
  { name: 'Fade in / out', gradient: { alphaKeys: [[0, 0], [0.2, 1], [0.8, 1], [1, 0]] } },
  { name: 'Fire', gradient: { colorKeys: [[0, 1, 0.95, 0.6], [0.35, 1, 0.55, 0.1], [1, 0.35, 0.05, 0.02]], alphaKeys: [[0, 1], [0.7, 0.8], [1, 0]] } },
  { name: 'Smoke', gradient: { colorKeys: [[0, 0.55, 0.55, 0.55], [1, 0.25, 0.25, 0.25]], alphaKeys: [[0, 0], [0.15, 0.6], [1, 0]] } },
  { name: 'Magic', gradient: { colorKeys: [[0, 0.4, 0.9, 1], [0.5, 0.75, 0.35, 1], [1, 1, 0.4, 0.8]], alphaKeys: [[0, 1], [1, 0]] } },
  {
    name: 'Rainbow',
    gradient: { colorKeys: [[0, 1, 0, 0], [0.2, 1, 1, 0], [0.4, 0, 1, 0], [0.6, 0, 1, 1], [0.8, 0, 0, 1], [1, 1, 0, 1]] },
  },
];

const css = (c: Rgba) =>
  `rgb(${Math.round(clamp01(c[0]) * 255)} ${Math.round(clamp01(c[1]) * 255)} ${Math.round(clamp01(c[2]) * 255)} / ${r3(clamp01(c[3]))})`;

export const cssColor = css;

/** CSS `linear-gradient` that shows the gradient exactly (hard stops in fixed mode). */
export function gradientCss(g: Gradient | undefined, withAlpha = true): string {
  const times = [
    ...new Set([0, 1, ...(g?.colorKeys ?? []).map((k) => clamp01(k[0])), ...(g?.alphaKeys ?? []).map((k) => clamp01(k[0]))]),
  ].sort((a, b) => a - b);
  const col = (t: number) => {
    const c = evalGradient(g, t);
    return withAlpha ? c : ([c[0], c[1], c[2], 1] as Rgba);
  };
  const stops: string[] = [];
  if (g?.mode === 'fixed') {
    for (let i = 1; i < times.length; i++) {
      const t0 = times[i - 1]!;
      const t1 = times[i]!;
      const c = css(col(t1));
      stops.push(`${c} ${r3(t0 * 100)}%`, `${c} ${r3(t1 * 100)}%`);
    }
  } else {
    for (const t of times) stops.push(`${css(col(t))} ${r3(t * 100)}%`);
  }
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
